import axios from 'axios';
import { Membership, MembershipCreditPack, ProductOffering, StripeCheckoutTransaction } from '../types';
import { attachAuthInterceptor } from './authToken';

const BASE_URL = (process.env.REACT_APP_CLUBHOUSE_API_URL || 'http://localhost:5000').replace(/\/+$/, '');

const apiClient = axios.create({
  baseURL: `${BASE_URL}/api`,
});
attachAuthInterceptor(apiClient);

export interface CancelMembershipResponse {
  success: boolean;
  membership?: Membership | null;
  message?: string | null;
  supportEmail: string;
}

/**
 * Golfer-facing membership client. Mirrors the iOS `MembershipAPI` and the `.NET`
 * `MembershipController`: read the plan catalog and the current membership, and switch to a
 * no-cost tier. Paid upgrades on the web go through Stripe Checkout (added separately); the
 * API rejects a direct change to a paid tier.
 */
export const membershipApi = {
  startCancellation: async (userId: string): Promise<string> => {
    const { data } = await apiClient.post<{ url: string }>(
      `/membership/${encodeURIComponent(userId)}/cancellation-session`,
    );
    return data.url;
  },

  syncBilling: async (userId: string): Promise<Membership> => {
    const { data } = await apiClient.post<Membership>(
      `/membership/${encodeURIComponent(userId)}/billing/sync`,
    );
    return data;
  },

  /** The active Range and Club membership catalog. */
  getProducts: async (): Promise<ProductOffering[]> => {
    const { data } = await apiClient.get<ProductOffering[]>('/membership/products');
    return data;
  },

  /** Active one-time lie-upload packs shown before the membership plans. */
  getCreditPacks: async (): Promise<MembershipCreditPack[]> => {
    const { data } = await apiClient.get<MembershipCreditPack[]>('/membership/credit-packs');
    return data;
  },

  /** The signed-in golfer's current membership (Free when they've never subscribed). */
  get: async (userId: string): Promise<Membership> => {
    const { data } = await apiClient.get<Membership>(`/membership/${encodeURIComponent(userId)}`);
    return data;
  },

  /** Completed Stripe Checkout transactions for this golfer's Clubhouse account. */
  getTransactions: async (userId: string): Promise<StripeCheckoutTransaction[]> => {
    const { data } = await apiClient.get<StripeCheckoutTransaction[]>(
      `/membership/${encodeURIComponent(userId)}/transactions`,
    );
    return data;
  },

  /**
   * Switches to a no-cost tier (e.g. downgrade to Free). The API rejects paid tiers with 400 —
   * those require a completed payment (Stripe on web, App Store on iOS).
   */
  changeToFree: async (userId: string, productOfferingId: string, cycle = 'Monthly'): Promise<Membership> => {
    const { data } = await apiClient.post<Membership>(
      `/membership/${encodeURIComponent(userId)}/change`,
      { productOfferingId, cycle },
    );
    return data;
  },

  /** Cancels Stripe billing and moves the golfer to Range Membership after Stripe succeeds. */
  cancel: async (userId: string, reason?: string, rating?: number | null): Promise<CancelMembershipResponse> => {
    try {
      const { data } = await apiClient.post<CancelMembershipResponse>(
        `/membership/${encodeURIComponent(userId)}/cancel`,
        { reason: reason?.trim() || null, rating: rating ?? null },
      );
      return data;
    } catch (error) {
      if (axios.isAxiosError<CancelMembershipResponse>(error)
        && error.response?.status === 502
        && error.response.data?.supportEmail) {
        return error.response.data;
      }
      throw error;
    }
  },

  /** Creates Stripe Checkout for a paid plan, a one-time credit pack, or both. */
  startCheckout: async (userId: string, productOfferingId?: string | null, creditPackId?: string | null): Promise<string> => {
    const { data } = await apiClient.post<{ url: string }>(
      `/membership/${encodeURIComponent(userId)}/checkout`,
      { productOfferingId, creditPackId },
    );
    return data.url;
  },

  /** Confirms the returned Stripe Checkout session and applies its paid plan. */
  confirmCheckout: async (userId: string, sessionId: string): Promise<Membership> => {
    const { data } = await apiClient.post<Membership>(
      `/membership/${encodeURIComponent(userId)}/checkout/${encodeURIComponent(sessionId)}/confirm`,
    );
    return data;
  },
};
